import { RichNotification } from "../notifications/RichNotification";
import { buttonCornerRadius } from "../styles/buttonCorners";
import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import { FirebaseMessaging } from "@capacitor-firebase/messaging";
import { App as CapacitorApp } from "@capacitor/app";
import { useAuth } from "../auth/AuthProvider";
import { BrandLogo } from "../components/BrandLogo";
import { CachedProfileAvatar } from "../components/CachedProfileAvatar";
import { startOfflineAutoUpdates } from "../trips/tripOffline";
import { usePlan } from "../billing/PlanProvider";
import { Home } from "./HomePage";
import { useI18n } from "../i18n/I18nProvider";
const SettingsPage = lazy(() => import("../settings/SettingsPage").then((module) => ({ default: module.SettingsPage })));
const TripsPage = lazy(() => import("../trips/TripsPage").then((module) => ({ default: module.TripsPage })));
import { deleteNotification, markNotificationRead, subscribeNotifications } from "../notifications/notificationService";
import type { InAppNotification } from "../notifications/notificationTypes";
import { previewTripInvitation, respondTripInvitation } from "../trips/tripInvitations";
import { applyTripWorkspaceSection, setSelectedTripId, tripWorkspaceSectionIds, upsertTripSummary } from "../trips/tripStore";
import { startUserTripListSync } from "../trips/tripCloudSync";
const AiAssistantPage = lazy(() => import("../ai/AiAssistantPage").then((module) => ({ default: module.AiAssistantPage })));
const MyWorldSection = lazy(() => import("../profile/MyWorldSection").then((module) => ({ default: module.MyWorldSection })));
import { refreshGamification } from "../profile/gamificationService";
import { logAppLifecycle, logScreenView, logUserAction, setAnalyticsConsent, setCrashReportingConsent, startAnalyticsSession } from "../utils/telemetry";

import type { SettingsPageId } from "../settings/SettingsPage";

type Tab = "home" | "trips" | "world" | "ai" | "settings";

const tabs: Array<{ id: Tab; key: "nav.home" | "nav.trips" | "nav.world" | "nav.ai" | "nav.menu" }> = [
  { id: "home", key: "nav.home" },
  { id: "trips", key: "nav.trips" },
  { id: "world", key: "nav.world" },
  { id: "ai", key: "nav.ai" },
  { id: "settings", key: "nav.menu" }
];

function NavigationIcon({ tab }: { tab: Tab }) {
  const pathByTab: Record<Tab, ReactNode> = {
    home: <path d="M4 10.5 12 4l8 6.5V19a1 1 0 0 1-1 1h-5v-5H10v5H5a1 1 0 0 1-1-1z" />,
    trips: <><path d="M3 13.5h6l4.5 6.5h2l-2.5-6.5H21a1.5 1.5 0 0 0 0-3h-8L15.5 4h-2L9 10.5H3a1.5 1.5 0 0 0 0 3Z" /></>,
    world: <><circle cx="12" cy="12" r="8" /><path d="M4.8 9h14.4" /><path d="M4.8 15h14.4" /><path d="M12 4a12.5 12.5 0 0 1 0 16" /><path d="M12 4a12.5 12.5 0 0 0 0 16" /></>,
    ai: <><path d="M12 3.5 14.5 9l5.5 2.5-5.5 2.5L12 19.5 9.5 14 4 11.5 9.5 9 12 3.5Z" /></>,
    settings: <><path d="M5 7h14M5 12h14M5 17h14" /></>
  };
  return (
    <span className={`nav-icon nav-icon--${tab}`} aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none">{pathByTab[tab]}</svg>
    </span>
  );
}

export function AppShell() {
  const { user, profile } = useAuth();
  const { complete } = usePlan();
  const { t } = useI18n();
  const [tab, setTab] = useState<Tab>("home");
  const [worldInitialTab,setWorldInitialTab]=useState<"map"|"countries">("map");
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationInvitationId, setNotificationInvitationId] = useState<string | null>(null);
  const [settingsEntry, setSettingsEntry] = useState<SettingsPageId>("menu");
  const [settingsKey, setSettingsKey] = useState(0);
  const [tripEntry, setTripEntry] = useState<{ mode: "default" | "list" | "create"; token: number }>({ mode: "default", token: 0 });
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [tripListVersion, setTripListVersion] = useState(0);
  const [backExitWarning, setBackExitWarning] = useState(false);
  const [activeTripTitle, setActiveTripTitle] = useState("");
  useEffect(() => {
    if (!user?.uid || !complete) return;
    return startOfflineAutoUpdates(user.uid);
  }, [user?.uid, complete]);
  const tabHistoryRef = useRef<Tab[]>([]);
  const backStageRef = useRef(0);


  useEffect(() => {
    if (tab !== "settings") return;
    const content = document.querySelector<HTMLElement>("main.content");
    if (!content) return;
    let frame = 0;
    const measure = () => {
      const tool = content.querySelector<HTMLElement>(".calculator-page");
      if (!tool) return;
      const nav = document.querySelector<HTMLElement>(".mobile-nav");
      const viewport = window.visualViewport;
      const bottom = viewport ? viewport.height + viewport.offsetTop : innerHeight;
      const boundary = nav && getComputedStyle(nav).display !== "none" ? Math.min(bottom,nav.getBoundingClientRect().top) : bottom;
      tool.style.setProperty("--mundely-tool-available-height",`${Math.max(0,boundary-tool.getBoundingClientRect().top-content.scrollTop-18)}px`);
    };
    const schedule = () => { cancelAnimationFrame(frame); frame=requestAnimationFrame(measure); };
    const resizeObserver = new ResizeObserver(schedule);
    resizeObserver.observe(content);
    const nav=document.querySelector<HTMLElement>(".mobile-nav"),topbar=document.querySelector<HTMLElement>(".topbar");
    if(nav)resizeObserver.observe(nav);if(topbar)resizeObserver.observe(topbar);
    const observer = new MutationObserver(schedule);
    observer.observe(content,{childList:true,subtree:true});
    window.addEventListener("resize",schedule);
    window.visualViewport?.addEventListener("resize",schedule);
    schedule();
    return () => {cancelAnimationFrame(frame);observer.disconnect();resizeObserver.disconnect();window.removeEventListener("resize",schedule);window.visualViewport?.removeEventListener("resize",schedule);};
  },[tab,settingsKey]);

  const unreadCount = notifications.filter((item) => !item.readAt).length;
  const topbarTitle = tab === "trips"
    ? activeTripTitle
    : (tab === "world" || tab === "settings" ? t("world.eyebrow") : "");

  useEffect(() => {
    const analyticsEnabled = profile?.preferences.analyticsEnabled === true;
    void setCrashReportingConsent(analyticsEnabled);
    void setAnalyticsConsent(analyticsEnabled).then(() => {
      if (analyticsEnabled) void startAnalyticsSession(profile?.plan || "unknown");
    });
  }, [profile?.preferences.analyticsEnabled, profile?.plan]);

  useEffect(() => {
    const analyticsEnabled = profile?.preferences.analyticsEnabled === true;
    if (!analyticsEnabled) return;
    void logScreenView(tab);
  }, [tab, profile?.preferences.analyticsEnabled]);

  useEffect(() => {
    const analyticsEnabled = profile?.preferences.analyticsEnabled === true;
    if (!analyticsEnabled || tab !== "settings") return;
    void logScreenView(`settings_${settingsEntry}`, "settings");
  }, [tab, settingsEntry, profile?.preferences.analyticsEnabled]);

  useEffect(() => {
    const analyticsEnabled = profile?.preferences.analyticsEnabled === true;
    if (!analyticsEnabled) return;
    const onVisibility = () => void logAppLifecycle(document.hidden ? "background" : "foreground");
    document.addEventListener("visibilitychange", onVisibility);
    let cancelled = false;
    let remove: (() => Promise<void>) | undefined;
    void CapacitorApp.addListener("appStateChange", ({ isActive }) => {
      void logAppLifecycle(isActive ? "foreground" : "background");
    }).then((handle) => {
      if (cancelled) void handle.remove();
      else remove = () => handle.remove();
    }).catch(() => undefined);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      if (remove) void remove();
    };
  }, [profile?.preferences.analyticsEnabled]);

  useEffect(() => {
    const buttonColor = "var(--primary)";
    const buttonRadius = `${buttonCornerRadius(profile?.preferences.buttonRadius)}px`;
    document.documentElement.style.setProperty("--trip-button-color", buttonColor);
    document.documentElement.style.setProperty("--trip-button-radius", buttonRadius);
  }, [profile?.preferences.buttonColor, profile?.preferences.buttonRadius]);

  function scrollCurrentPageToTop() {
    window.scrollTo(0, 0);
    const content = document.querySelector(".content");
    if (content instanceof HTMLElement) content.scrollTo({ top: 0, left: 0, behavior: "auto" });
    const nestedScrollers = document.querySelectorAll<HTMLElement>(".settings-page, .trips-page, .trip-workspace, .my-world-page, .ai-assistant-page");
    nestedScrollers.forEach((element) => {
      if (element.scrollTop > 0) element.scrollTop = 0;
    });
  }

  function scheduleScrollToTop() {
    window.requestAnimationFrame(scrollCurrentPageToTop);
  }

  function resetBackSequence() {
    backStageRef.current = 0;
    setBackExitWarning(false);
  }

  function navigateTab(next: Tab) {
    setTab((current) => {
      if (current !== next) {
        tabHistoryRef.current.push(current);
        void logUserAction("tab_change", "navigation", "ok", { source: current, target: next });
      }
      return next;
    });
    resetBackSequence();
    scheduleScrollToTop();
  }

  useEffect(() => {
    const openAi = () => navigateTab("ai");
    window.addEventListener("mundely:open-ai", openAi);
    return () => window.removeEventListener("mundely:open-ai", openAi);
  }, []);

  useEffect(() => {
    const explore = (event: Event) => {
      if (!user) return;
      const detail = (event as CustomEvent<{name?:string;id?:string}>).detail;
      const destination = String(detail?.name || "").trim().slice(0,120);
      if (!destination) return;
      // Dedicated AI inbox: NextTripPage also reads the older shared destination key.
      sessionStorage.setItem("mundely.ai.destination.prompt.v2", JSON.stringify({uid:user.uid,destination,at:Date.now()}));
      navigateTab("ai");
      window.dispatchEvent(new CustomEvent("mundely:destination-prompt", { detail: { uid: user.uid, destination } }));
    };
    window.addEventListener("mundely:explore-destination",explore);
    return () => window.removeEventListener("mundely:explore-destination",explore);
  },[user?.uid]);

  useEffect(()=>{
    const openSource=(event:Event)=>{
      const target=(event as CustomEvent<{target?:string}>).detail?.target;
      if(target==="countries"){setWorldInitialTab("countries");navigateTab("world")}
      else if(target==="completed"){sessionStorage.setItem("mundely.trips.focus","completed");openTrips("list")}
      else if(target==="reviews"||target==="groups"){setSettingsEntry(target==="reviews"?"myReviews":"tripGroups");setSettingsKey(value=>value+1);navigateTab("settings")}
    };
    window.addEventListener("mundely:traveler-source",openSource);
    return()=>window.removeEventListener("mundely:traveler-source",openSource);
  },[]);

  function openTrips(mode: "default" | "list" | "create") {
    void logUserAction("open_trips", "trips", "ok", { mode });
    setTripEntry((current) => ({ mode, token: current.token + 1 }));
    navigateTab("trips");
  }

  useEffect(() => {
    if (!user) return;
    return startUserTripListSync({
      uid: user.uid,
      onChange: () => setTripListVersion((value) => value + 1)
    });
  }, [user?.uid]);

  useEffect(() => {
    if (!user) return;
    const openWidgetTarget = (targetValue?: string | null, tripValue?: string | null) => {
      const target = String(targetValue || "").trim();
      if (!target) return;
      if (target === "plan") { localStorage.removeItem("mundely.widget.target"); setSettingsEntry("plan"); setSettingsKey(value => value + 1); setTab("settings"); scheduleScrollToTop(); return; }
      if (target.startsWith("widgetSettings:")) {
        const rawSettingsTarget = target.slice("widgetSettings:".length).trim();
        const [kind, rawWidgetId] = rawSettingsTarget.split(":");
        const widgetId = Number(rawWidgetId || 0);
        if (kind) localStorage.setItem("mundely.widget.settings.kind", kind);
        if (kind && Number.isFinite(widgetId) && widgetId > 0) {
          localStorage.setItem("mundely.widget.settings.instance", JSON.stringify({ kind, id: widgetId, at: Date.now() }));
        } else {
          localStorage.removeItem("mundely.widget.settings.instance");
        }
        localStorage.removeItem("mundely.widget.target");
        setSettingsEntry("widgets");
        setSettingsKey((value) => value + 1);
        setTab("settings");
        resetBackSequence();
        scheduleScrollToTop();
        return;
      }
      const tripId = String(tripValue || localStorage.getItem("mundely.widget.tripId") || "").trim();
      if (tripId) setSelectedTripId(user.uid, tripId);
      localStorage.setItem("mundely.widget.target", target);
      if (tripId) localStorage.setItem("mundely.widget.tripId", tripId);
      setTripEntry((current) => ({ mode: "default", token: current.token + 1 }));
      setTab("trips");
      resetBackSequence();
      scheduleScrollToTop();
    };
    const pending = localStorage.getItem("mundely.widget.target");
    if (pending) window.setTimeout(() => openWidgetTarget(pending, localStorage.getItem("mundely.widget.tripId")), 0);
    const onWidgetOpen = (event: Event) => {
      const detail = (event as CustomEvent<{ target?: string; tripId?: string }>).detail;
      openWidgetTarget(detail?.target, detail?.tripId);
    };
    window.addEventListener("mundely:widget-open", onWidgetOpen as EventListener);
    return () => window.removeEventListener("mundely:widget-open", onWidgetOpen as EventListener);
  }, [user?.uid]);

  useEffect(() => {
    const handleTitle = (event: Event) => {
      const detail = (event as CustomEvent<{ title?: string }>).detail;
      setActiveTripTitle(String(detail?.title || ""));
    };
    window.addEventListener("tripai:active-trip-title", handleTitle as EventListener);
    return () => window.removeEventListener("tripai:active-trip-title", handleTitle as EventListener);
  }, []);

  useEffect(() => {
    if (tab !== "trips") setActiveTripTitle("");
  }, [tab]);

  useEffect(() => {
    const reset = () => {
      resetBackSequence();
      scheduleScrollToTop();
    };
    window.addEventListener("tripai:navigation-forward", reset);
    return () => window.removeEventListener("tripai:navigation-forward", reset);
  }, []);

  useEffect(() => {
    scheduleScrollToTop();
  }, [tab, settingsKey, tripEntry.token]);

  useEffect(() => {
    let cancelled = false;
    let remove: (() => Promise<void>) | undefined;
    void CapacitorApp.addListener("backButton", () => {
      if (backStageRef.current >= 2) {
        void CapacitorApp.exitApp();
        return;
      }
      if (backStageRef.current === 1) {
        backStageRef.current = 2;
        setBackExitWarning(true);
        return;
      }

      const detail = { handled: false };
      window.dispatchEvent(new CustomEvent("tripai:back-request", { detail }));
      if (!detail.handled) {
        const previous = tabHistoryRef.current.pop();
        if (previous) setTab(previous);
      }
      backStageRef.current = 1;
      setBackExitWarning(false);
    }).then((handle) => {
      if (cancelled) void handle.remove();
      else remove = () => handle.remove();
    }).catch(() => undefined);
    return () => { cancelled = true; if (remove) void remove(); };
  }, []);

  useEffect(() => {
    if (!user) {
      setNotifications([]);
      return;
    }
    return subscribeNotifications(user.uid, setNotifications);
  }, [user?.uid]);

  useEffect(() => {
    if (!user || !profile) return;
    let timer: number | undefined;
    let disposed = false;
    const run = () => {
      if (disposed) return;
      void refreshGamification(user.uid, profile).catch(() => undefined);
    };
    const schedule = (event?: Event) => {
      const detail = event ? (event as CustomEvent<{ uid?: string }>).detail : undefined;
      if (detail?.uid && detail.uid !== user.uid) return;
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(run, 250);
    };
    schedule();
    window.addEventListener("tripai:gamification-source-updated", schedule as EventListener);
    return () => {
      disposed = true;
      if (timer) window.clearTimeout(timer);
      window.removeEventListener("tripai:gamification-source-updated", schedule as EventListener);
    };
  }, [user?.uid, profile?.updatedAt, profile?.username, profile?.photoUrl]);

  useEffect(() => {
    const viewport = window.visualViewport;
    const root = document.documentElement;
    const stableViewportHeight = Math.max(window.innerHeight, viewport?.height || 0);
    let focusTimer: number | undefined;

    const updateViewportState = () => {
      const active = document.activeElement as HTMLElement | null;
      const tag = active?.tagName || "";
      const editable = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || active?.isContentEditable === true;
      const currentViewportHeight = viewport?.height || window.innerHeight;
      const reducedViewport = stableViewportHeight - currentViewportHeight > 120 ||
        (viewport ? window.innerHeight - viewport.height > 120 : false);
      const isKeyboardOpen = Boolean(editable && reducedViewport);

      // Android/WebView may resize only the visual viewport when the soft keyboard opens.
      // Publish the real visible height globally so every screen can size itself against
      // the same source of truth instead of stacking fixed bottom paddings.
      root.style.setProperty("--trip-visual-viewport-height", `${Math.round(currentViewportHeight)}px`);
      root.classList.toggle("trip-keyboard-open", isKeyboardOpen);
      setKeyboardOpen(isKeyboardOpen);
    };

    const scheduleUpdate = () => {
      if (focusTimer) window.clearTimeout(focusTimer);
      focusTimer = window.setTimeout(updateViewportState, 40);
    };

    updateViewportState();
    viewport?.addEventListener("resize", updateViewportState);
    viewport?.addEventListener("scroll", updateViewportState);
    window.addEventListener("resize", updateViewportState);
    document.addEventListener("focusin", scheduleUpdate);
    document.addEventListener("focusout", scheduleUpdate);

    return () => {
      if (focusTimer) window.clearTimeout(focusTimer);
      viewport?.removeEventListener("resize", updateViewportState);
      viewport?.removeEventListener("scroll", updateViewportState);
      window.removeEventListener("resize", updateViewportState);
      document.removeEventListener("focusin", scheduleUpdate);
      document.removeEventListener("focusout", scheduleUpdate);
      root.classList.remove("trip-keyboard-open");
      root.style.removeProperty("--trip-visual-viewport-height");
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    let remove: (() => Promise<void>) | undefined;
    void FirebaseMessaging.addListener("notificationActionPerformed", (event) => {
      const data = (event.notification?.data || {}) as Record<string, unknown>;
      if (String(data.kind || "") !== "tripInvitation") return;
      const invitationId = String(data.invitationId || "");
      if (invitationId) setNotificationInvitationId(invitationId);
      setNotificationsOpen(true);
    }).then((handle) => {
      if (cancelled) void handle.remove();
      else remove = () => handle.remove();
    }).catch(() => undefined);
    return () => { cancelled = true; if (remove) void remove(); };
  }, []);


  useEffect(() => {
    const openPlan = () => { setSettingsEntry("plan"); setSettingsKey(value => value + 1); navigateTab("settings"); };
    window.addEventListener("mundely:open-plan", openPlan);
    return () => window.removeEventListener("mundely:open-plan", openPlan);
  }, []);

  return (
    <div className="app-frame">
      <aside className="desktop-sidebar">
        <BrandLogo />
        <nav>
          {tabs.map((item) => (
            <button key={item.id} className={tab === item.id ? "nav-item active" : "nav-item"} aria-current={tab === item.id ? "page" : undefined} onClick={() => {
              if (item.id === "settings") {
                setSettingsEntry("menu");
                setSettingsKey((value) => value + 1);
              }
              if (item.id === "trips") { openTrips("list"); return; }
              navigateTab(item.id);
            }}>
              <NavigationIcon tab={item.id} /><span>{t(item.key)}</span>
            </button>
          ))}
        </nav>
      </aside>

      <div className="app-main">
        <header className="topbar">
          <div className="mobile-brand">{tab === "home" ? <img className="brand-logo" src="/brand/mundely_logo_home.png" alt="Mundely" /> : <BrandLogo compact />}</div>
          <div className={topbarTitle ? "topbar-trip-title visible" : "topbar-trip-title"}>{topbarTitle}</div>
          <div className="topbar-actions">
            <button className="icon-button notification-button" type="button" onClick={() => setNotificationsOpen(true)} aria-label={t("home.notifications")}>
              <svg className="notification-bell-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4" /></svg>
              {unreadCount > 0 && <span className="notification-badge" aria-label={`${unreadCount}`}>{unreadCount > 99 ? "99+" : unreadCount}</span>}
            </button>
            <button
              className="avatar-button"
              type="button"
              aria-label={t("settings.profile")}
              title={t("settings.profile")}
              onClick={() => {
                void logUserAction("open_profile", "navigation");
                setSettingsEntry("profile");
                setSettingsKey((value) => value + 1);
                navigateTab("settings");
              }}
            >
              <CachedProfileAvatar uid={user?.uid || ""} url={profile?.photoUrl} fallback={profile?.firstName || "T"} />
            </button>
          </div>
        </header>

        <main className={tab === "ai" ? "content ai-content" : "content"}>
          {tab === "home" && <Home
            syncVersion={tripListVersion}
            onOpenSettings={(page) => { setSettingsEntry(page); setSettingsKey((value) => value + 1); navigateTab("settings"); }}
            onOpenWorld={() => navigateTab("world")}
            onOpenAi={() => navigateTab("ai")}
            onChooseTrip={() => openTrips("list")}
            onNewTrip={() => openTrips("create")}
            onOpenTrip={(tripId, target) => {
              sessionStorage.removeItem("mundely.home.target");
              if (user && target) sessionStorage.setItem("mundely.home.target", JSON.stringify({ uid: user.uid, tripId, target, at: Date.now() }));
              if (user) setSelectedTripId(user.uid, tripId);
              openTrips("default");
            }}
          />}
          <Suspense fallback={<div className="loading-screen"><img src="/brand/mundely_backpack_icon.png" alt="Mundely" /></div>}>
          {tab === "trips" && <TripsPage entryMode={tripEntry.mode} entryToken={tripEntry.token} />}
          {tab === "world" && <MyWorldSection initialTab={worldInitialTab} />}
          {tab === "ai" && <AiAssistantPage syncVersion={tripListVersion} />}
          {tab === "settings" && <SettingsPage key={settingsKey} initialPage={settingsEntry} />}
          </Suspense>
        </main>

        <nav className={keyboardOpen ? "mobile-nav keyboard-hidden" : "mobile-nav"}>
          {tabs.map((item) => (
            <button key={item.id} className={tab === item.id ? "active" : ""} aria-current={tab === item.id ? "page" : undefined} onClick={() => {
              if (item.id === "settings") {
                setSettingsEntry("menu");
                setSettingsKey((value) => value + 1);
              }
              if (item.id === "trips") { openTrips("list"); return; }
              navigateTab(item.id);
            }}>
              <NavigationIcon tab={item.id} /><small>{t(item.key)}</small>
            </button>
          ))}
        </nav>
      </div>

      {backExitWarning && <div className="app-back-exit-warning" role="status">{t("app.backExitWarning")}</div>}
      {notificationsOpen && <NotificationCenter items={notifications} initialInvitationId={notificationInvitationId} onClose={() => { setNotificationsOpen(false); setNotificationInvitationId(null); }} onOpenTrip={() => { openTrips("default"); setNotificationsOpen(false); }} />}
    </div>
  );
}


function EmptyPage({ title, body, action }: { title: string; body: string; action?: string }) {
  return (
    <section className="empty-page">
      <div className="empty-icon">✦</div>
      <h1>{title}</h1>
      <p>{body}</p>
      {action && <button className="button primary">{action}</button>}
    </section>
  );
}

function NotificationCenter({ items, onClose, onOpenTrip, initialInvitationId }: { items: InAppNotification[]; onClose: () => void; onOpenTrip: () => void; initialInvitationId?: string | null }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const [openingTrip, setOpeningTrip] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [resolvedInvitationIds, setResolvedInvitationIds] = useState<Record<string, true>>({});
  const [localReadIds, setLocalReadIds] = useState<Record<string, true>>({});

  useEffect(() => {
    if (!initialInvitationId || items.length === 0) return;
    const match = items.find((item) => item.data?.invitationId === initialInvitationId);
    if (match) setDetailsId(match.id);
  }, [initialInvitationId, items]);

  async function markRead(item: InAppNotification) {
    if (!user) return;
    setLocalReadIds((current) => ({ ...current, [item.id]: true }));
    try {
      await markNotificationRead(user.uid, item.id);
    } catch {
      setLocalReadIds((current) => {
        const next = { ...current };
        delete next[item.id];
        return next;
      });
      setError(t("errors.generic"));
    }
  }

  async function enterInvitation(item: InAppNotification) {
    if (!user || !item.data?.invitationId) return;
    setOpeningTrip(true);
    setError("");
    try {
      const result = await previewTripInvitation(item.data.invitationId);
      upsertTripSummary(user.uid, result.trip);
      for (const section of tripWorkspaceSectionIds) {
        if (Object.prototype.hasOwnProperty.call(result.workspace, section)) {
          applyTripWorkspaceSection(user.uid, result.trip.id, section, result.workspace[section]);
        }
      }
      sessionStorage.setItem("tripai.invitePreview", JSON.stringify({ invitationId: item.data.invitationId, tripId: result.trip.id }));
      setSelectedTripId(user.uid, result.trip.id);
      await markRead(item);
      onOpenTrip();
    } catch {
      setError(t("notifications.inviteResponseError"));
    } finally {
      setOpeningTrip(false);
    }
  }

  async function respond(item: InAppNotification, decision: "accepted" | "declined") {
    if (!user || !item.data?.invitationId) return;
    const invitationId = item.data.invitationId;
    setBusyId(item.id);
    setError("");

    let result: Awaited<ReturnType<typeof respondTripInvitation>>;
    try {
      result = await respondTripInvitation(invitationId, decision);
    } catch (cause) {
      console.error("Trip invitation response failed", { invitationId, decision, cause });
      setError(t("notifications.inviteResponseError"));
      setBusyId(null);
      return;
    }

    // From this point the backend has recorded the decision. Local cache or notification
    // cleanup problems must never be reported as a failed invitation response.
    setResolvedInvitationIds((current) => ({ ...current, [invitationId]: true }));
    if (decision === "accepted") setFeedback(t("notifications.acceptedPopup"));

    if (decision === "accepted" && result.trip) {
      try {
        upsertTripSummary(user.uid, result.trip);
      } catch (cause) {
        console.error("Accepted trip could not be written to the local cache", { invitationId, cause });
      }
    }

    await markRead(item);
    setBusyId(null);
  }

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <aside className="notification-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-header"><h2>{t("notifications.title")}</h2><button className="icon-button" onClick={onClose} aria-label={t("common.close")}>×</button></div>
        {error && <div className="settings-error">{error}</div>}
        {feedback && <div className="notification-feedback"><strong>{feedback}</strong><button className="icon-button" type="button" onClick={() => setFeedback("")} aria-label={t("common.close")}>
            <span aria-hidden="true">×</span>
          </button></div>}
        {items.length === 0 ? <div className="empty-notifications">{t("notifications.empty")}</div> : (
          <div className="notification-list">
            {items.map((item) => {
              const invitationId = item.data?.invitationId || "";
              const isRead = Boolean(item.readAt || localReadIds[item.id]);
              const isInvitation =
                item.data?.kind === "tripInvitation" &&
                Boolean(invitationId) &&
                !item.data?.invitationDecision &&
                !resolvedInvitationIds[invitationId];
              return (
                <article className={isRead ? "notification-item" : "notification-item unread"} key={item.id}>
                  <div>
                    <strong>{t(item.titleKey as Parameters<typeof t>[0])}</strong>
                    {item.richContent ? <RichNotification value={item.richContent} labels={{unavailable:t("notifications.contentUnavailable"),video:t("notifications.video"),openVideo:t("notifications.openVideo")}} /> : <p>{t(item.bodyKey as Parameters<typeof t>[0])}</p>}
                    {item.data?.tripName && <b className="notification-trip-name">{item.data.tripName}</b>}
                    {item.data?.inviterName && <small>{t("notifications.invitedBy", { name: item.data.inviterName })}</small>}
                    {item.data?.participantRole && <small>{t("notifications.role", { role: t(`participants.role.${item.data.participantRole}` as Parameters<typeof t>[0]) })}</small>}
                    {(item.data?.notes || item.data?.note || item.data?.invitationNote) && <small className="notification-invite-note"><b>{t("participants.notes")}:</b> {item.data.notes || item.data.note || item.data.invitationNote}</small>}
                    {item.data?.personName && <small>{item.data.personName}</small>}
                    {isInvitation && detailsId === item.id && (
                      <div className="notification-trip-details">
                        {item.data?.destination && <span><b>{t("trips.destination")}:</b> {item.data.destination}</span>}
                        {(item.data?.startDate || item.data?.endDate) && <span><b>{t("notifications.dates")}:</b> {item.data.startDate || "—"} → {item.data.endDate || "—"}</span>}
                      </div>
                    )}
                  </div>
                  <div className="notification-actions">
                    {isInvitation ? (
                      <>
                        <button className="button secondary compact" type="button" disabled={openingTrip} onClick={() => void enterInvitation(item)}>{t("notifications.openTrip")}</button>
                        <button className="button primary compact" disabled={busyId === item.id} onClick={() => void respond(item, "accepted")}>{t("notifications.acceptInvite")}</button>
                        <button className="button secondary compact" disabled={busyId === item.id} onClick={() => void respond(item, "declined")}>{t("notifications.declineInvite")}</button>
                      </>
                    ) : (
                      !isRead && <button className="link-button" onClick={() => void markRead(item)}>{t("notifications.markRead")}</button>
                    )}
                    {!isInvitation && <button className="link-button danger-text" onClick={() => void deleteNotification(user!.uid, item.id)}>{t("common.delete")}</button>}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </aside>
    </div>
  );
}
